import React, { useState, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Typography,
  Card,
  Tag,
  message,
  Tooltip,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  AudioOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { SourceEpisode, SourcePanel, SourceProcess, SourceScene } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { sourceStoriesApi } from '../../services/api/sourceStories';
import { fileService } from '../../services/fileService';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';
import EditEpisodeModal from './EditEpisodeModal';
import EpisodePreviewModal from './EpisodePreviewModal';
import EpisodeContentModal from './EpisodeContentModal';
import VideoEditorModal from './VideoEditorModal';

const { Title, Text } = Typography;

function panelMetaFromEpisode(episode: SourceEpisode, panelId: string | undefined) {
  if (!panelId || !episode.panels?.length) return null;
  return episode.panels.find((p) => p.panelId === panelId) ?? null;
}

function buildPanelRangeLines(
  panelId: string | undefined,
  episode: SourceEpisode,
  panelMap: Map<string, SourcePanel>,
  sceneMap: Map<string, SourceScene>,
  processMap: Map<string, SourceProcess>
): { summary: string; detail: string } {
  if (!panelId) {
    return { summary: '—', detail: '' };
  }

  const panel = panelMap.get(panelId);
  const meta = panelMetaFromEpisode(episode, panelId);

  if (!panel) {
    const ord = meta?.orderIndex;
    const shortId = `${panelId.slice(0, 8)}…`;
    const summary =
      ord != null ? `Episode panel #${ord} (loading…)` : `Panel ${shortId}`;
    return { summary, detail: '' };
  }

  const scene = sceneMap.get(panel.sceneId);
  const processId = scene?.processId ?? panel.processId;
  const proc = processId ? processMap.get(processId) : undefined;
  const processName = proc?.name ?? 'Process';
  const sceneTitle = scene?.title ?? 'Scene';
  const sceneNum = scene?.orderIndex ?? '?';

  // Table: Process > Scene #n: title > Panel #m
  const summary = `${processName} > Scene #${sceneNum}: ${sceneTitle} > Panel #${panel.sceneOrderIndex}`;

  const detail = [
    `Process:${processName}`,
    `Scene:#${sceneNum} ${sceneTitle}`,
    `Panel:#${panel.sceneOrderIndex}`,
  ].join('\n');

  return { summary, detail };
}

/** Ant Design Tooltip ignores \\n in string titles; pre-line makes each block break correctly */
function RangeTooltipTitle({ text }: { text: string }) {
  return (
    <div style={{ whiteSpace: 'pre-line', maxWidth: 320, textAlign: 'left' }}>
      {text}
    </div>
  );
}

interface EpisodesTabProps {
  storyId: string;
}

const EpisodesTab: React.FC<EpisodesTabProps> = ({ storyId }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<SourceEpisode | null>(null);
  const [previewEpisode, setPreviewEpisode] = useState<SourceEpisode | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [contentEpisode, setContentEpisode] = useState<SourceEpisode | null>(null);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [videoEditorEpisode, setVideoEditorEpisode] = useState<SourceEpisode | null>(null);
  const [isVideoEditorVisible, setIsVideoEditorVisible] = useState(false);
  const [generatingAudioId, setGeneratingAudioId] = useState<string | null>(null);

  const { execute: executeAsync } = useAsyncOperation();

  const { data: processesData } = useApi(
    useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]),
    { immediate: !!storyId }
  );
  const { data: scenesData } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourceScenes(storyId), [storyId]),
    { immediate: !!storyId }
  );
  const { data: panelsData } = useApi(
    useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]),
    { immediate: !!storyId }
  );

  const processMap = useMemo(() => {
    const list = processesData?.data ?? [];
    return new Map<string, SourceProcess>(list.map((p) => [p.id, p]));
  }, [processesData]);

  const sceneMap = useMemo(() => {
    const list = scenesData?.data ?? [];
    return new Map<string, SourceScene>(list.map((s) => [s.id, s]));
  }, [scenesData]);

  const panelMap = useMemo(() => {
    const list = panelsData?.data ?? [];
    return new Map<string, SourcePanel>(list.map((p) => [p.id, p]));
  }, [panelsData]);

  // Fetch episodes
  const {
    data: episodesData,
    loading: episodesLoading,
    refresh: refreshEpisodes,
  } = useApi(
    useCallback(() => sourceEpisodesApi.getEpisodes(storyId), [storyId]),
    { immediate: true }
  );

  const handleCreate = () => {
    setEditingEpisode(null);
    setIsModalVisible(true);
  };

  const handleEdit = (episode: SourceEpisode) => {
    setEditingEpisode(episode);
    setIsModalVisible(true);
  };

  const handleDelete = async (episode: SourceEpisode) => {
    await executeAsync(
      () => sourceEpisodesApi.deleteEpisode(episode.id),
      { successMessage: 'Episode deleted successfully!' }
    );
    refreshEpisodes();
  };
  
  const handleDownload = async (episode: SourceEpisode) => {
    try {
      message.loading({ content: 'Preparing download...', key: 'download' });
      const blob = await sourceEpisodesApi.exportEpisodeMaterials(episode.id);
      fileService.saveFile(blob, `episode-${episode.id}-export.zip`);
      message.success({ content: 'Download complete!', key: 'download', duration: 2 });
    } catch (error) {
      message.error({ content: 'Failed to download materials', key: 'download', duration: 2 });
      console.error('Download error:', error);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Tag color="default">Draft</Tag>;
      case 'PUBLISHED':
        return <Tag color="success">Published</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getAudioStatusTag = (status: string | undefined) => {
    switch (status) {
      case 'generating':
        return <Tag icon={<SyncOutlined spin />} color="processing">Generating</Tag>;
      case 'ready':
        return <Tag icon={<CheckCircleOutlined />} color="success">Ready</Tag>;
      case 'error':
        return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
      default:
        return <Tag color="default">No Audio</Tag>;
    }
  };

  const handleGenerateAudio = async (episode: SourceEpisode) => {
    setGeneratingAudioId(episode.id);
    const msgKey = `gen-audio-${episode.id}`;
    message.loading({ content: 'Đang tạo audio tổng cho episode (có thể mất 1-2 phút)...', key: msgKey, duration: 0 });
    
    try {
      // Gọi trực tiếp API thay vì qua executeAsync để bắt được lỗi chi tiết
      const response = await sourceEpisodesApi.generateMasterAudio(storyId, episode.id);
      
      if (response && response.data) {
        message.success({ content: 'Đã tạo xong audio tổng cho episode!', key: msgKey, duration: 3 });
        refreshEpisodes();
      } else {
        message.error({ content: 'Lỗi: Phản hồi từ server không hợp lệ.', key: msgKey, duration: 3 });
      }
    } catch (error: any) {
      console.error('Audio generation error:', error);
      // Lấy message lỗi chi tiết từ response của Backend (ErrorModel format)
      const errorData = error.response?.data;
      const errorMessage = errorData?.messages?.[0] || errorData?.message || error.message || 'Không rõ nguyên nhân';
      message.error({ content: `Lỗi: ${errorMessage}`, key: msgKey, duration: 5 });
    } finally {
      setGeneratingAudioId(null);
    }
  };

  const handlePlayMasterAudio = (url: string) => {
    Modal.info({
      title: 'Playing Master Audio',
      width: 500,
      content: (
        <div style={{ marginTop: 20 }}>
          <audio controls src={url} autoPlay style={{ width: '100%' }}>
            Your browser does not support the audio element.
          </audio>
        </div>
      ),
      okText: 'Close',
    });
  };

  const columns = [
    {
      title: 'Order',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 80,
      render: (index: number) => <Tag color="blue">{index}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: SourceEpisode) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.thumbnailUrl && (
            <div style={{ marginTop: 4, width: 80, aspectRatio: '16/9', overflow: 'hidden', borderRadius: 4 }}>
              <img src={record.thumbnailUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: SourceEpisode) => (
        <Space direction="vertical" size={4}>
          {getStatusTag(status)}
          {getAudioStatusTag(record.audioStatus)}
        </Space>
      ),
    },
    {
      title: 'Range',
      key: 'range',
      width: 380,
      render: (_: unknown, record: SourceEpisode) => {
        const start = buildPanelRangeLines(record.startPanelId, record, panelMap, sceneMap, processMap);
        const end = buildPanelRangeLines(record.endPanelId, record, panelMap, sceneMap, processMap);
        return (
          <Space direction="vertical" size={4}>
            <Tooltip title={start.detail ? <RangeTooltipTitle text={start.detail} /> : undefined}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>Start: </Text>
                {start.summary}
              </Text>
            </Tooltip>
            <Tooltip title={end.detail ? <RangeTooltipTitle text={end.detail} /> : undefined}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>End: </Text>
                {end.summary}
              </Text>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Last Modified',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: SourceEpisode) => (
        <Space>
          <Tooltip title="Preview">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setPreviewEpisode(record);
                setIsPreviewVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View Content">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              onClick={() => {
                setContentEpisode(record);
                setIsContentVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Video Editor">
            <Button
              type="text"
              icon={<VideoCameraOutlined />}
              onClick={async () => {
                // Fetch fresh episode data from API
                const freshEpisode = await sourceEpisodesApi.getEpisodeDetail(record.id);
                setVideoEditorEpisode(freshEpisode.data);
                setIsVideoEditorVisible(true);
              }}
              disabled={!record.masterAudioUrl || record.audioStatus !== 'ready'}
            />
          </Tooltip>
          <Tooltip title="Generate Master Audio">
            <Button
              type="text"
              icon={<AudioOutlined />}
              onClick={() => handleGenerateAudio(record)}
              loading={record.audioStatus === 'generating' || generatingAudioId === record.id}
              disabled={!record.startPanelId || !record.endPanelId || (!!generatingAudioId && generatingAudioId !== record.id)}
            />
          </Tooltip>
          {record.masterAudioUrl && record.audioStatus === 'ready' && (
            <Tooltip title="Listen Master Audio">
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                onClick={() => handlePlayMasterAudio(record.masterAudioUrl!)}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          )}
          <Tooltip title="Download Materials">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this episode?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Episode Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Episode
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={episodesData?.data || []}
        rowKey="id"
        loading={episodesLoading}
      />

      <EditEpisodeModal
        visible={isModalVisible}
        episode={editingEpisode}
        storyId={storyId}
        onClose={() => {
          setIsModalVisible(false);
          setEditingEpisode(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setEditingEpisode(null);
          refreshEpisodes();
        }}
        nextOrderIndex={(episodesData?.data?.length || 0) + 1}
      />

      {previewEpisode && (
        <EpisodePreviewModal
          visible={isPreviewVisible}
          episode={{...previewEpisode, storyId}}
          onClose={() => {
            setIsPreviewVisible(false);
            setPreviewEpisode(null);
          }}
        />
      )}

      {videoEditorEpisode && (
        <VideoEditorModal
          visible={isVideoEditorVisible}
          episode={videoEditorEpisode}
          storyId={storyId}
          onClose={() => {
            setIsVideoEditorVisible(false);
            setVideoEditorEpisode(null);
          }}
          onSuccess={async (updatedEpisode?: SourceEpisode) => {
            await refreshEpisodes();
            if (updatedEpisode) {
              setVideoEditorEpisode(updatedEpisode);
            } else {
              // Refresh episode data from API
              const refreshed = await sourceEpisodesApi.getEpisodeDetail(videoEditorEpisode.id);
              setVideoEditorEpisode(refreshed.data);
            }
          }}
        />
      )}

      {contentEpisode && (
        <EpisodeContentModal
          visible={isContentVisible}
          episode={contentEpisode}
          onClose={() => {
            setIsContentVisible(false);
            setContentEpisode(null);
          }}
        />
      )}
    </Card>
  );
};

export default EpisodesTab;
